// packages/core/src/learning/school-modules/behavioral-finance.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'behavioral-finance' school. LearningModule, surface 'school'.
//   module id   = 'school-behavioral-finance'  (matches exhibit nativeModuleId)
//   homeSchool  = 'behavioral-finance'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_BEHAVIORAL_FINANCE_MODULE: LearningModule = {
	id: 'school-behavioral-finance',
	title: 'Behavioral Finance',
	description:
		'The lens that studies the gap between what you know and what you do under pressure — overconfidence, loss aversion, herd behavior, and systems over willpower.',
	emoji: '🧠',
	accentColor: '#7b5ea7',
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 0,
	surface: 'school',
	homeSchool: 'behavioral-finance',
	lessons: [
		{
			id: 'school-behavioral-finance-01',
			title: 'You are not a spreadsheet',
			description:
				'Learn why behavioral finance starts with the gap between what people know and what they actually do.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['behavioral-finance'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['behavioral-finance', 'behavior-gap', 'plan-realism'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['behavior gap', 'emotion and money', 'plan realism'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Most people do not make money mistakes because they have never heard of saving, debt, investing, or budgeting. They make mistakes because real life puts pressure on the plan. Stress shows up. Friends spend money. Markets fall. A bill hits. A craving wins.',
				},
				{
					type: 'paragraph',
					text: 'Behavioral finance believes the spreadsheet is only part of the story. The other part is the human being trying to follow it. If the plan ignores fear, shame, ego, boredom, family pressure, and temptation, the plan is not complete.',
				},
				{
					type: 'bullet_list',
					items: [
						'What do you know?',
						'What do you actually do?',
						'What pressure changes your behavior?',
						'What system helps you stay steady?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is assuming good information automatically creates good behavior.',
				},
				{
					type: 'example',
					text: 'Someone knows they want to save $300 this month. The math works. Then stress hits after work, food delivery feels deserved, a friend invites them out, and the money leaks away. Behavioral finance does not call them stupid. It asks why the plan had no protection against normal human pressure.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that behavior can become a lazy explanation if used wrong. Not every struggle is mindset. Low income, family obligations, health issues, rent, debt, and unstable work are real constraints. Behavior matters, but it is not the whole world.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens asks you to compare your intentions with your actual Dashboard. The point is not shame. The point is pattern recognition.',
				},
				{
					type: 'key_takeaway',
					text: 'A money plan has to fit the person who will actually live it.',
				},
			],
			audioScript:
				'Behavioral finance starts with a truth that is uncomfortable but useful: you are not a spreadsheet. You can understand a money idea and still fail to follow it when life gets loud. That does not mean you are broken. It means you are human.\n\nA spreadsheet can assume you will save the same amount every month, spend exactly what you planned, invest calmly, ignore hype, never panic, and never buy something because you had a bad day. Real people do not work that way. Real people get tired. They get lonely. They compare themselves to friends. They want relief. They want status. They want safety. They want control. Money decisions get mixed with emotion because money is not just numbers. Money touches identity, fear, family, freedom, and self respect.\n\nThink about a simple savings goal. You decide you want to save $300 this month. The math says you can do it. Your rent is covered, your bills are covered, and you have enough income. Then the month happens. You buy food because cooking feels exhausting. You say yes to a night out because you do not want to feel left behind. You make an impulse purchase because you have been disciplined all week and feel like you earned it. By the end of the month, the $300 is gone, and the easy reaction is shame.\n\nBehavioral finance tries to be more useful than shame. It asks what happened between the plan and the result. Was the plan too tight? Did you need a spending category for real life? Did you have no pause before impulse buys? Did the money sit too visibly in checking, making it feel available? Did you make decisions at night when you were tired? Did social pressure beat your system?\n\nThis lens is powerful because it looks at behavior without pretending people are robots. But it has a blind spot too. It can go wrong if every money problem gets reduced to mindset. Some people are not failing because they lack discipline. They may have rent that is too high, wages that are too low, family obligations, medical issues, unstable hours, or debt pressure. Behavioral finance should never be used to say, “Just think better.” That is shallow and unfair.\n\nThe better use is this: respect the real constraints, then build systems for the behavior you actually have. Maybe your money needs to move automatically before you can spend it. Maybe you need a rule to wait before large purchases. Maybe you need to check your Dashboard every week, not every three months. Maybe you need a plan that includes joy so you do not rebel against it.\n\nInside Nisk, this means looking at your real numbers and real patterns together. Not to beat yourself up. To find the gap between what you meant to do and what the month actually did. That gap is where the lesson lives.',
			flashcards: [
				{
					id: 'school-behavioral-finance-01-fc-1',
					role: 'main_idea',
					front: 'What is the behavior gap?',
					back: 'The gap between what you know and what you actually do.',
				},
				{
					id: 'school-behavioral-finance-01-fc-2',
					role: 'watch_out',
					front: 'What does knowledge not guarantee?',
					back: 'It does not guarantee steady behavior under pressure.',
				},
				{
					id: 'school-behavioral-finance-01-fc-3',
					role: 'use_it',
					front: 'How do you use this lens?',
					back: 'Compare your plan with your real patterns.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Compare plan and reality',
			},
			revision: 1,
		},
		{
			id: 'school-behavioral-finance-02',
			title: 'Overconfidence makes risk feel smaller',
			description:
				'Learn how behavioral finance sees confidence as useful until it hides uncertainty and downside.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['behavioral-finance'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['behavioral-finance', 'overconfidence', 'humility'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'overconfidence risk',
				'false certainty',
				'humility under pressure',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Confidence feels good. It makes decisions feel clean. It makes risk feel manageable. It makes your plan feel smarter than everyone else’s. That is exactly why behavioral finance treats overconfidence as dangerous.',
				},
				{
					type: 'paragraph',
					text: 'Behavioral finance believes people often overrate their ability to predict, time, control, or outsmart complex situations. The problem is not confidence itself. The problem is confidence that stops asking what could go wrong.',
				},
				{
					type: 'bullet_list',
					items: [
						'What am I assuming?',
						'What would prove me wrong?',
						'What happens if timing is bad?',
						'Am I confusing luck with skill?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is feeling safe because the story sounds convincing.',
				},
				{
					type: 'example',
					text: 'Someone makes a risky money move after watching a few confident creators explain it. The story is simple, the upside sounds obvious, and everyone around them sounds certain. Behavioral finance notices the danger: certainty can spread faster than understanding.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that too much humility can turn into paralysis. You still have to make choices with incomplete information. The goal is not to doubt everything. The goal is to leave room for being wrong.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to simulation. A decision feels different when you test the downside instead of only imagining the upside.',
				},
				{
					type: 'key_takeaway',
					text: 'Confidence is useful only when it still leaves room for being wrong.',
				},
			],
			audioScript:
				'Overconfidence is one of the most expensive feelings in money. It does not always feel like arrogance. Sometimes it feels like clarity. Sometimes it feels like momentum. Sometimes it feels like finally understanding something everyone else missed. That is what makes it dangerous.\n\nBehavioral finance pays attention to the way confidence changes how risk feels. When you are confident, uncertainty shrinks in your mind. The bad outcome feels unlikely. The upside feels obvious. The people warning you sound timid. The people agreeing with you sound smart. A decision that might have deserved caution starts feeling easy.\n\nThis shows up everywhere. Someone thinks they can time the market because they guessed right once. Someone thinks a business idea cannot fail because the story makes sense. Someone takes on a car payment because future income feels certain. Someone puts too much money into one opportunity because they believe they understand it better than most people. Sometimes these decisions work. But behavioral finance asks a sharper question: did it work because the decision was wise, or because the outcome happened to be lucky?\n\nThat distinction matters. Luck can teach the wrong lesson. If a risky decision works once, the brain may say, “I knew it.” That feeling can become dangerous because it makes the next risk feel safer than it really is. The person may increase the bet, ignore warning signs, or start believing that confidence is the same as skill.\n\nOverconfidence also spreads socially. A confident creator, friend, investor, coworker, or online community can make a shaky idea feel normal. When enough people speak with certainty, uncertainty starts to feel embarrassing. You may start thinking, “Maybe I am the only one who does not get it.” That is when behavioral finance tells you to pause. Certainty is not proof. A simple story is not proof. A crowd is not proof.\n\nThe correction is not to become frozen. Too much doubt can create its own problem. You still have to make choices in a world where you will never know everything. The healthier move is to build humility into the decision. Ask what you are assuming. Ask what would prove you wrong. Ask what happens if timing is bad. Ask whether you can survive being mistaken.\n\nInside Nisk, simulation helps because it forces the confident story to meet numbers. What if the upside takes longer? What if the cost is higher? What if income changes? What if the choice fails and you need time to recover? You are not trying to predict the future perfectly. You are testing whether your plan depends on being right.\n\nBehavioral finance does not hate confidence. It hates untested confidence. Strong judgment is not loud certainty. Strong judgment is confidence with a backup plan.',
			flashcards: [
				{
					id: 'school-behavioral-finance-02-fc-1',
					role: 'main_idea',
					front: 'Why is overconfidence risky?',
					back: 'It makes uncertainty and downside feel smaller than they are.',
				},
				{
					id: 'school-behavioral-finance-02-fc-2',
					role: 'watch_out',
					front: 'What can luck teach badly?',
					back: 'It can make a risky choice feel like skill.',
				},
				{
					id: 'school-behavioral-finance-02-fc-3',
					role: 'use_it',
					front: 'How do you check confidence?',
					back: 'Test what happens if your assumption is wrong.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test the downside',
			},
			revision: 1,
		},
		{
			id: 'school-behavioral-finance-03',
			title: 'Losses feel louder than gains',
			description:
				'Learn why behavioral finance cares about panic, loss aversion, and bad timing.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['behavioral-finance'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['behavioral-finance', 'loss-aversion', 'panic-timing'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['loss aversion', 'panic timing', 'emotional selling'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Losing money often hurts more than gaining the same amount feels good. That emotional imbalance can distort decisions. You may know the plan, but a loss can make the plan feel wrong in your body.',
				},
				{
					type: 'paragraph',
					text: 'Behavioral finance believes losses are not just numbers. They create fear, regret, embarrassment, and urgency. When those feelings take over, people can make timing decisions that damage the very plan they were trying to protect.',
				},
				{
					type: 'bullet_list',
					items: [
						'What did I lose?',
						'What am I afraid happens next?',
						'Am I reacting to the plan or the pain?',
						'What rule did I write before this moment?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is changing the plan during the emotional peak of a loss.',
				},
				{
					type: 'example',
					text: 'Someone invests with a long view when things are calm. Then the account drops. The original plan has not changed, but the feeling has. Behavioral finance notices that panic can turn a long view into a short reaction.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that not every loss should be ignored. Sometimes a loss reveals a bad assumption, too much risk, or a plan that never fit. The lesson is not to stay forever. The lesson is to avoid letting panic be the only voice in the room.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens asks you to write rules before pressure shows up. Calm rules are often better than emergency feelings.',
				},
				{
					type: 'key_takeaway',
					text: 'Losses are loud, so your rules need to be written before the noise starts.',
				},
			],
			audioScript:
				'Behavioral finance pays close attention to losses because losses do not feel neutral. A loss can feel like danger, failure, regret, embarrassment, or proof that you were foolish. Even when the dollar amount is manageable, the emotion can be loud. That loudness changes decisions.\n\nA common idea in behavioral finance is that people often feel losses more strongly than similar gains. In plain English, losing $500 can hurt more than gaining $500 feels good. That imbalance matters because money plans often require patience. Saving, investing, paying down debt, building a career, and improving cash flow all involve moments that feel uncomfortable. If every uncomfortable moment creates an emergency reaction, the plan can break even if the math still works.\n\nThink about investing. Someone starts with a calm plan. They know markets can move. They know the time horizon is not one week. They know there will be drops. Then the drop actually happens. The account balance falls. The news gets loud. Friends start talking. Social media becomes dramatic. Suddenly, the calm plan feels naive. The person does not just see a lower number. They feel the loss. They feel the possibility that it could get worse. They feel the regret of not acting sooner. That emotional state can push them into a decision they would not have made while calm.\n\nThis does not only happen with investing. A business idea loses money at first, and someone quits too early. A career move feels uncertain, and someone runs back before the opportunity had time to develop. A budget gets messed up for one week, and someone says the whole month is ruined. Losses can create all or nothing thinking.\n\nThe point is not to ignore every loss. That would be reckless. Sometimes a loss is information. Maybe the plan was too risky. Maybe the assumption was wrong. Maybe the purchase was not affordable. Maybe the investment did not fit your needs. Behavioral finance is not saying, “Never change your mind.” It is saying, “Do not let panic be the only decision maker.”\n\nThat is why rules matter. Rules written in calm moments can protect you during loud moments. Before risk shows up, you can decide what would make you pause, what would make you review, what would make you change course, and what you will not do during panic. A rule might be as simple as waiting twenty four hours before a large emotional purchase. It might be reviewing your numbers before selling something in fear. It might be checking whether the original reason for the plan has actually changed.\n\nInside Nisk, this lens pairs well with reflection. Write the rule before the pressure. Your future self may not be calm enough to invent the rule in the moment. Losses are loud. A written rule gives your quieter judgment a way to speak.',
			flashcards: [
				{
					id: 'school-behavioral-finance-03-fc-1',
					role: 'main_idea',
					front: 'Why do losses distort decisions?',
					back: 'Losses often feel louder than similar gains.',
				},
				{
					id: 'school-behavioral-finance-03-fc-2',
					role: 'watch_out',
					front: 'What is the panic timing trap?',
					back: 'Changing the plan at the emotional peak of fear.',
				},
				{
					id: 'school-behavioral-finance-03-fc-3',
					role: 'use_it',
					front: 'What helps during losses?',
					back: 'Use rules written before pressure shows up.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write your panic rule',
				prompt: 'Write what you will review before making a major money decision during fear, regret, envy, or panic.',
			},
			revision: 1,
		},
		{
			id: 'school-behavioral-finance-04',
			title: 'The crowd makes risk feel normal',
			description:
				'Learn how behavioral finance sees herd behavior, social proof, and money pressure.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['behavioral-finance'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['behavioral-finance', 'herd-behavior', 'social-proof'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'herd behavior',
				'social proof risk',
				'comparison pressure',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'It is easier to make a risky decision when everyone around you is doing it. The crowd makes risk feel normal. It can turn uncertainty into belonging.',
				},
				{
					type: 'paragraph',
					text: 'Behavioral finance believes social proof has power. If friends, coworkers, creators, or online communities all talk the same way, your brain may treat popularity like evidence. But a decision can be popular and still be wrong for your numbers.',
				},
				{
					type: 'bullet_list',
					items: [
						'Who is influencing me?',
						'Do their numbers match mine?',
						'What pressure am I feeling?',
						'Would I still choose this alone?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is borrowing someone else’s confidence without borrowing their situation.',
				},
				{
					type: 'example',
					text: 'A friend group starts upgrading apartments, cars, trips, and wardrobes. Nobody says, “Spend more.” But the standard shifts. Behavioral finance notices that comparison can raise your costs without ever calling itself a financial decision.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that other people can also help you learn. Community is not the enemy. The blind spot is treating the crowd as proof instead of context.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens asks you to test the choice against your real numbers. Social pressure gets weaker when the tradeoff becomes visible.',
				},
				{
					type: 'key_takeaway',
					text: 'A popular decision still has to survive your actual life.',
				},
			],
			audioScript:
				'The crowd has a strange power over money decisions. Something can feel risky when you consider it alone, then feel completely normal once enough people around you are doing it. That is herd behavior in everyday clothing. It does not always look like a stock mania or a viral investment trend. Sometimes it looks like everyone getting a nicer apartment. Everyone financing a newer car. Everyone taking trips. Everyone ordering food. Everyone saying a certain opportunity is obvious. Everyone acting like a certain lifestyle is just what adults do now.\n\nBehavioral finance pays attention to this because humans are social. We look to other people for cues, especially when the decision is uncertain. If you do not know what is normal, you look around. If everyone seems confident, you may feel safer. If everyone is spending, you may feel behind. If everyone is investing in something, you may feel foolish for hesitating. The crowd does not have to force you. It only has to make a choice feel normal.\n\nThe problem is that other people do not live with your numbers. They may have different income, family support, debt, savings, housing costs, risk tolerance, obligations, or goals. They may also be pretending. You often see the purchase, not the payment plan. You see the trip, not the credit card balance. You see the confidence, not the stress. Borrowing someone else’s confidence without borrowing their situation is dangerous.\n\nThis can show up in quiet ways. Your friend group starts going out more, and your food spending rises. Your coworkers talk about cars, and your paid off car starts feeling embarrassing. People online show apartments, outfits, trips, and investments, and your normal life starts feeling like failure. No one told you to change your spending. But the standard moved, and your money followed.\n\nThe tradeoff is that community is not bad. Other people can teach you, challenge you, and expose you to better ideas. A good community can help you save, invest, negotiate, pay down debt, or think bigger. Behavioral finance is not telling you to ignore everyone. It is telling you to separate social proof from personal fit.\n\nA useful question is, “Would I still want this if nobody saw it?” Another is, “Would I still choose this if I had to explain the tradeoff to my future self?” Those questions bring the decision back to your life instead of the crowd’s mood.\n\nInside Nisk, the way to weaken social pressure is to make the tradeoff visible. Test the choice against your actual numbers. What happens to your savings rate? What happens to your runway? What happens to your future options? When you see the cost clearly, the crowd loses some of its control.\n\nA popular decision is not automatically wrong. But popularity is not proof. Your numbers still get a vote.',
			flashcards: [
				{
					id: 'school-behavioral-finance-04-fc-1',
					role: 'main_idea',
					front: 'What does the crowd change?',
					back: 'It can make risk and spending feel normal.',
				},
				{
					id: 'school-behavioral-finance-04-fc-2',
					role: 'watch_out',
					front: 'What is borrowed confidence?',
					back: 'Copying someone’s certainty without sharing their situation.',
				},
				{
					id: 'school-behavioral-finance-04-fc-3',
					role: 'use_it',
					front: 'How do you weaken social pressure?',
					back: 'Test the choice against your real numbers.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test the social pressure',
			},
			revision: 1,
		},
		{
			id: 'school-behavioral-finance-05',
			title: 'Systems beat willpower',
			description:
				'Learn why behavioral finance prefers defaults, rules, and friction over relying on perfect discipline.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['behavioral-finance'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['behavioral-finance', 'decision-design', 'friction'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'decision design',
				'money defaults',
				'friction as protection',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Willpower is real, but it gets tired. It works better in the morning than at midnight. It works better after sleep than after stress. It works better when temptation is far away than when the purchase button is already glowing.',
				},
				{
					type: 'paragraph',
					text: 'Behavioral finance believes better systems can protect you from weaker moments. Defaults, rules, reminders, pauses, and friction matter because they shape behavior before emotion takes over.',
				},
				{
					type: 'bullet_list',
					items: [
						'Make good choices easier',
						'Make bad choices slower',
						'Decide rules while calm',
						'Review patterns without shame',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is trying to solve repeated behavior with repeated motivation.',
				},
				{
					type: 'example',
					text: 'Someone keeps spending the money they meant to save because it stays in checking. A better system moves money out of sight after payday. The person did not become a new human. The decision environment changed.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that systems can become avoidance if you never review them. Automation without attention can hide leaks. Rules without flexibility can become too rigid.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens asks you to write money rules that match your real behavior. Do not build the system for your imaginary perfect self. Build it for the person who gets tired.',
				},
				{
					type: 'key_takeaway',
					text: 'Discipline gets easier when your system stops asking for hero mode every day.',
				},
			],
			audioScript:
				'Behavioral finance does not worship willpower. It respects it, but it does not build the whole plan on it. That matters because willpower changes. You may feel disciplined on Sunday night while planning the week. You may feel very different on Thursday after work when you are tired, hungry, annoyed, and one click away from spending money you meant to save.\n\nA lot of money advice acts like the answer is to try harder. Behavioral finance pushes back. If the same mistake keeps happening, the problem may not be your character. The problem may be your decision environment. The system around you might be making the wrong choice too easy and the right choice too dependent on mood.\n\nThink about saving. If all of your money sits in one checking account, every dollar can feel available. You have to keep deciding not to spend it. Every purchase becomes a little test of discipline. That is exhausting. A better system might move savings away soon after income lands. Now the choice is different. You are not relying on heroic self control every time you open your banking app. The system made the intended behavior easier.\n\nFriction matters too. Friction means making a choice slightly slower on purpose. That might mean deleting a shopping app, waiting a day before purchases over a certain amount, keeping a written rule near your budget, removing saved cards from websites, or checking your Dashboard before making a big change. Friction is not punishment. It is protection against a moment that might not represent your real priorities.\n\nDefaults matter because people often stick with what happens automatically. If the default is spending what remains in checking, that default has power. If the default is saving first, reviewing weekly, or pausing before big purchases, that default has power too. Behavioral finance cares about defaults because they shape behavior quietly.\n\nThe tradeoff is that systems can go stale. Automation can hide problems. A rule that helped last year may not fit this year. A system can become too rigid if it leaves no room for joy, family, emergencies, or changing goals. The answer is not to set rules and disappear. The answer is to build rules, then review them.\n\nInside Nisk, this lens becomes practical. Look at the behaviors you keep repeating. Do not just say, “I need more discipline.” Ask what the system is currently encouraging. Is money too visible? Is spending too easy? Is saving too manual? Are emotional purchases too fast? Are reviews too rare?\n\nBuild the system for your real self, not your perfect self. Your real self gets tired. Your real self gets tempted. Your real self needs fewer daily battles. Discipline gets much easier when your environment stops demanding hero mode.',
			flashcards: [
				{
					id: 'school-behavioral-finance-05-fc-1',
					role: 'main_idea',
					front: 'Why do systems beat willpower?',
					back: 'Systems protect behavior when motivation gets tired.',
				},
				{
					id: 'school-behavioral-finance-05-fc-2',
					role: 'watch_out',
					front: 'What is the motivation trap?',
					back: 'Trying to fix repeated behavior with repeated hype.',
				},
				{
					id: 'school-behavioral-finance-05-fc-3',
					role: 'use_it',
					front: 'How do you apply this?',
					back: 'Make good choices easier and bad choices slower.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Design your money system',
				prompt: 'Write one money behavior you keep repeating, then design one rule or friction point that would make the better choice easier.',
			},
			revision: 1,
		},
	],
};
